import { Home, Grid, ShoppingCart, User, Search } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCartSync";
import { useLanguage } from "@/hooks/useLanguage";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getTotalItems, setIsOpen } = useCart();
  const { t } = useLanguage();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleSearchClick = () => {
    // Redirige vers la page de recherche
    navigate('/search');
  };

  const handleCartClick = () => {
    // Ouvre le panier (sidebar)
    setIsOpen(true);
  };

  const navItems = [
    { 
      icon: Home, 
      label: t('header.home'), 
      path: '/',
      active: location.pathname === '/',
      showLabel: true
    },
    { 
      icon: Grid, 
      label: t('header.all_products'), 
      path: '/products',
      active: location.pathname === '/products',
      showLabel: true
    },
    { 
      icon: Search, 
      label: t('search'), 
      onClick: handleSearchClick,
      active: location.pathname === '/search',
      showLabel: false
    },
    { 
      icon: ShoppingCart, 
      label: t('header.cart'), 
      onClick: handleCartClick,
      active: false,
      badge: getTotalItems() || null,
      showLabel: false
    },
    { 
      icon: User, 
      label: user ? t('header.my_profile') : t('header.login'), 
      path: user ? '/dashboard' : '/auth',
      active: location.pathname === '/dashboard' || location.pathname === '/auth',
      showLabel: false
    },
  ];

  // Évite le rendu avant que les hooks soient initialisés
  if (!isMounted) {
    return null;
  }

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t shadow-lg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item, index) => {
          const content = (
            <div
              className={cn(
                "flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors relative min-w-[64px]",
                item.active 
                  ? "text-primary font-medium" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="relative">
                <item.icon className={cn(
                  "h-5 w-5",
                  item.active && "stroke-[2.5]"
                )} />
                {item.badge && (
                  <Badge className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 text-[10px]">
                    {item.badge}
                  </Badge>
                )}
              </div>
              {item.showLabel && (
                <span className={cn(
                  "text-[10px] leading-none",
                  item.active && "font-semibold"
                )}>
                  {item.label}
                </span>
              )}
            </div>
          );

          if (item.onClick) {
            return (
              <button
                key={index}
                onClick={item.onClick}
                className="focus:outline-none"
              >
                {content}
              </button>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path!}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
